import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MessageSquarePlus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  User,
  Mail,
  Calendar,
  ChevronDown,
  X,
  Save,
  ExternalLink,
  Bug,
  Lightbulb,
  ThumbsUp,
  HelpCircle,
  MoreHorizontal,
  Filter,
  Layout,
  MessageSquare,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { getSupabaseAdminClient } from '@parentpath/supabase-client';
import { useAuth } from '../auth/AuthContext';

const supabase = getSupabaseAdminClient();

// Types
type RequestType = 'question' | 'bug' | 'suggestion' | 'improvement' | 'praise' | 'other';

interface FeedbackCategory {
  id: string;
  name: string;
  slug: string;
}

interface BetaFeedback {
  id: string;
  user_id: string | null;
  feedback_category_id: string | null;
  request_type: RequestType;
  first_name: string;
  last_name: string;
  email: string;
  subject: string | null;
  message: string;
  status: 'pending' | 'in_progress' | 'resolved' | 'closed';
  admin_notes: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  created_at: string;
  updated_at: string;
}

// Config
const statusConfig = {
  pending: { label: 'Offen', icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
  in_progress: { label: 'In Bearbeitung', icon: AlertCircle, color: 'text-blue-600', bg: 'bg-blue-50' },
  resolved: { label: 'Gelöst', icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  closed: { label: 'Geschlossen', icon: XCircle, color: 'text-slate-600', bg: 'bg-slate-50' },
};

const requestTypeConfig: Record<RequestType, { label: string; icon: React.ElementType; bg: string; text: string }> = {
  bug: { label: 'Fehler', icon: Bug, bg: 'bg-red-100', text: 'text-red-700' },
  suggestion: { label: 'Wunsch', icon: Lightbulb, bg: 'bg-blue-100', text: 'text-blue-700' },
  improvement: { label: 'Tipp', icon: MessageSquare, bg: 'bg-amber-100', text: 'text-amber-700' },
  praise: { label: 'Lob', icon: ThumbsUp, bg: 'bg-green-100', text: 'text-green-700' },
  question: { label: 'Frage', icon: HelpCircle, bg: 'bg-purple-100', text: 'text-purple-700' },
  other: { label: 'Sonstiges', icon: MoreHorizontal, bg: 'bg-slate-100', text: 'text-slate-700' },
};

export function BetaFeedbackPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<RequestType | ''>('');
  const [selectedFeedback, setSelectedFeedback] = useState<BetaFeedback | null>(null);

  // Fetch feedback categories
  const { data: feedbackCategories } = useQuery({
    queryKey: ['admin-feedback-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feedback_categories')
        .select('id, name, slug')
        .order('sort_order');
      if (error) throw error;
      return data as FeedbackCategory[];
    },
  });

  // Fetch beta feedback (contact_requests with feedback_category_id set)
  const { data: feedbackList, isLoading } = useQuery({
    queryKey: ['admin-beta-feedback', statusFilter, typeFilter],
    queryFn: async () => {
      let query = supabase
        .from('contact_requests')
        .select('*')
        .not('feedback_category_id', 'is', null)
        .order('created_at', { ascending: false });

      if (statusFilter) {
        query = query.eq('status', statusFilter);
      }
      if (typeFilter) {
        query = query.eq('request_type', typeFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as BetaFeedback[];
    },
  });

  // Update feedback mutation
  const updateFeedback = useMutation({
    mutationFn: async (data: { id: string; status: string; admin_notes?: string }) => {
      const updates: Record<string, unknown> = {
        status: data.status,
        admin_notes: data.admin_notes,
      };

      if (data.status === 'resolved' || data.status === 'closed') {
        updates.resolved_at = new Date().toISOString();
        updates.resolved_by = user?.id;
      }

      const { error } = await (supabase.from('contact_requests') as any)
        .update(updates)
        .eq('id', data.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-beta-feedback'] });
      toast.success('Feedback aktualisiert');
    },
    onError: (error: Error) => {
      toast.error(`Fehler: ${error.message}`);
    },
  });

  const getFeedbackCategoryName = (feedbackCategoryId: string | null) => {
    if (!feedbackCategoryId) return null;
    return feedbackCategories?.find(c => c.id === feedbackCategoryId)?.name || null;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Stats by status
  const pendingCount = feedbackList?.filter(r => r.status === 'pending').length || 0;
  const inProgressCount = feedbackList?.filter(r => r.status === 'in_progress').length || 0;
  const resolvedCount = feedbackList?.filter(r => r.status === 'resolved').length || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
              <MessageSquarePlus className="w-6 h-6 text-teal-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Beta Feedback</h1>
              <p className="text-slate-500 mt-1">
                Feedback von Nutzern aus dem Beta-Formular
              </p>
            </div>
          </div>
        </div>

        {/* Status Stats */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div
            className={`rounded-lg p-4 cursor-pointer transition-colors ${
              statusFilter === '' && typeFilter === '' ? 'bg-slate-100 ring-2 ring-slate-400' : 'bg-slate-50 hover:bg-slate-100'
            }`}
            onClick={() => { setStatusFilter(''); setTypeFilter(''); }}
          >
            <div className="text-2xl font-bold text-slate-900">{feedbackList?.length || 0}</div>
            <div className="text-sm text-slate-500">Gesamt</div>
          </div>
          <div
            className={`rounded-lg p-4 cursor-pointer transition-colors ${
              statusFilter === 'pending' ? 'bg-amber-100 ring-2 ring-amber-500' : 'bg-amber-50 hover:bg-amber-100'
            }`}
            onClick={() => { setStatusFilter(statusFilter === 'pending' ? '' : 'pending'); setTypeFilter(''); }}
          >
            <div className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-600" />
              <span className="text-2xl font-bold text-amber-700">{pendingCount}</span>
            </div>
            <div className="text-sm text-amber-600">Offen</div>
          </div>
          <div
            className={`rounded-lg p-4 cursor-pointer transition-colors ${
              statusFilter === 'in_progress' ? 'bg-blue-100 ring-2 ring-blue-500' : 'bg-blue-50 hover:bg-blue-100'
            }`}
            onClick={() => { setStatusFilter(statusFilter === 'in_progress' ? '' : 'in_progress'); setTypeFilter(''); }}
          >
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <span className="text-2xl font-bold text-blue-700">{inProgressCount}</span>
            </div>
            <div className="text-sm text-blue-600">In Bearbeitung</div>
          </div>
          <div
            className={`rounded-lg p-4 cursor-pointer transition-colors ${
              statusFilter === 'resolved' ? 'bg-green-100 ring-2 ring-green-500' : 'bg-green-50 hover:bg-green-100'
            }`}
            onClick={() => { setStatusFilter(statusFilter === 'resolved' ? '' : 'resolved'); setTypeFilter(''); }}
          >
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-2xl font-bold text-green-700">{resolvedCount}</span>
            </div>
            <div className="text-sm text-green-600">Gelöst</div>
          </div>
        </div>

        {/* Type Stats */}
        <div className="border-t border-slate-100 pt-4">
          <h3 className="text-sm font-medium text-slate-500 mb-3 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Nach Feedback-Typ filtern
          </h3>
          <div className="flex flex-wrap gap-2">
            {(Object.keys(requestTypeConfig) as RequestType[]).map((type) => {
              const config = requestTypeConfig[type];
              const TypeIcon = config.icon;
              const count = feedbackList?.filter(r => r.request_type === type).length || 0;

              return (
                <button
                  key={type}
                  onClick={() => { setTypeFilter(typeFilter === type ? '' : type); setStatusFilter(''); }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    typeFilter === type
                      ? `${config.bg} ${config.text} ring-2 ring-offset-1 ring-current`
                      : `${config.bg} ${config.text} opacity-70 hover:opacity-100`
                  }`}
                >
                  <TypeIcon className="w-3.5 h-3.5" />
                  {config.label}
                  <span className="ml-1 opacity-60">({count})</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Feedback List */}
      <div className="bg-white rounded-lg shadow-sm divide-y divide-slate-100">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full mx-auto" />
          </div>
        ) : feedbackList?.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            <MessageSquarePlus className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <p>Kein Beta-Feedback vorhanden</p>
          </div>
        ) : (
          feedbackList?.map((feedback) => {
            const status = statusConfig[feedback.status];
            const StatusIcon = status.icon;
            const requestType = requestTypeConfig[feedback.request_type];
            const RequestTypeIcon = requestType.icon;
            const feedbackCatName = getFeedbackCategoryName(feedback.feedback_category_id);

            return (
              <div
                key={feedback.id}
                className="p-4 hover:bg-slate-50 cursor-pointer"
                onClick={() => setSelectedFeedback(feedback)}
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg ${status.bg} flex items-center justify-center flex-shrink-0`}>
                    <StatusIcon className={`w-5 h-5 ${status.color}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-medium text-slate-900">
                        {feedback.first_name} {feedback.last_name}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded ${status.bg} ${status.color}`}>
                        {status.label}
                      </span>
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${requestType.bg} ${requestType.text}`}>
                        <RequestTypeIcon className="w-3 h-3" />
                        {requestType.label}
                      </span>
                    </div>
                    {feedback.subject && (
                      <p className="text-sm font-medium text-slate-700 mb-1">
                        {feedback.subject}
                      </p>
                    )}
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                      {feedback.message}
                    </p>
                    <div className="flex items-center gap-4 text-xs text-slate-400 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3.5 h-3.5" />
                        {feedback.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(feedback.created_at)}
                      </span>
                      {feedbackCatName && (
                        <span className="flex items-center gap-1 bg-teal-50 text-teal-700 px-2 py-0.5 rounded">
                          <Layout className="w-3 h-3" />
                          {feedbackCatName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Detail Modal */}
      {selectedFeedback && (
        <FeedbackDetailModal
          feedback={selectedFeedback}
          feedbackCategoryName={getFeedbackCategoryName(selectedFeedback.feedback_category_id)}
          onClose={() => setSelectedFeedback(null)}
          onUpdate={(data) => {
            updateFeedback.mutate({ id: selectedFeedback.id, ...data });
            setSelectedFeedback(null);
          }}
          isUpdating={updateFeedback.isPending}
        />
      )}
    </div>
  );
}

// Detail Modal Component
interface FeedbackDetailModalProps {
  feedback: BetaFeedback;
  feedbackCategoryName: string | null;
  onClose: () => void;
  onUpdate: (data: { status: string; admin_notes?: string }) => void;
  isUpdating: boolean;
}

function FeedbackDetailModal({
  feedback,
  feedbackCategoryName,
  onClose,
  onUpdate,
  isUpdating
}: FeedbackDetailModalProps) {
  const [status, setStatus] = useState(feedback.status);
  const [adminNotes, setAdminNotes] = useState(feedback.admin_notes || '');

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onUpdate({ status, admin_notes: adminNotes || undefined });
  };

  const requestType = requestTypeConfig[feedback.request_type];
  const RequestTypeIcon = requestType.icon;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <h2 className="text-xl font-bold text-slate-900">Feedback-Details</h2>
            <span className={`inline-flex items-center gap-1 text-sm px-2.5 py-1 rounded ${requestType.bg} ${requestType.text}`}>
              <RequestTypeIcon className="w-4 h-4" />
              {requestType.label}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* User Info */}
            <div className="bg-slate-50 rounded-lg p-4">
              <h3 className="text-sm font-medium text-slate-500 mb-3">Kontaktdaten</h3>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <span className="font-medium">{feedback.first_name} {feedback.last_name}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <a
                    href={`mailto:${feedback.email}`}
                    className="text-teal-600 hover:underline"
                  >
                    {feedback.email}
                  </a>
                </div>
                {feedback.user_id && (
                  <Link
                    to={`/users?highlight=${feedback.user_id}`}
                    className="inline-flex items-center gap-1 text-sm text-teal-600 hover:underline mt-2"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Zur Benutzerverwaltung
                  </Link>
                )}
              </div>
            </div>

            {/* Feedback Info */}
            <div>
              <div className="flex items-center gap-4 mb-4 flex-wrap">
                {feedbackCategoryName && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-500">Bereich:</span>
                    <span className="bg-teal-50 text-teal-700 px-2 py-0.5 rounded text-sm flex items-center gap-1">
                      <Layout className="w-3.5 h-3.5" />
                      {feedbackCategoryName}
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-sm font-medium text-slate-500">Erstellt:</span>
                <span className="text-sm text-slate-600">{formatDate(feedback.created_at)}</span>
              </div>

              {feedback.subject && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-slate-500 mb-1">Betreff</h3>
                  <p className="text-slate-900 font-medium">{feedback.subject}</p>
                </div>
              )}

              <div className="bg-white border border-slate-200 rounded-lg p-4">
                <h3 className="text-sm font-medium text-slate-500 mb-2">Nachricht</h3>
                <p className="text-slate-700 whitespace-pre-wrap">{feedback.message}</p>
              </div>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Status
              </label>
              <div className="relative">
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as BetaFeedback['status'])}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg appearance-none
                             focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
                >
                  <option value="pending">Offen</option>
                  <option value="in_progress">In Bearbeitung</option>
                  <option value="resolved">Gelöst</option>
                  <option value="closed">Geschlossen</option>
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>

            {/* Admin Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Admin-Notizen (intern)
              </label>
              <textarea
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                rows={4}
                placeholder="Interne Notizen zum Feedback..."
                className="w-full px-4 py-2 border border-slate-200 rounded-lg resize-none
                           focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
              />
            </div>

            {feedback.resolved_at && (
              <div className="text-sm text-slate-500">
                Gelöst am: {formatDate(feedback.resolved_at)}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-slate-100 bg-slate-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={isUpdating}
              className="flex items-center gap-2 px-4 py-2 bg-teal-500 text-white rounded-lg
                         hover:bg-teal-600 disabled:opacity-50 transition-colors"
            >
              <Save className="w-4 h-4" />
              Speichern
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
